import React, { useState, useEffect } from 'react';
import { array, bool, func, string } from 'prop-types';

import { connect } from 'react-redux';

import { get } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as SiteActions from '../../actions/siteActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';
import { squareAreaUOMTypes } from '../../data/areaTypes';

const ValidationSchema = Yup.object().shape({
    client: Yup.string()
        .required(),
    site: Yup.string()
        .required(),
    name: Yup.string()
        .required(),
    squareAreaUOM: Yup.string(),
    squareFootage: Yup.string()
        .matches(/^([1-9]{1})([0-9]{0,8})$/, 'Area must be numeric.')
        .required()
});

const CreateAreaModal = ({
    close,
    clearErrors,
    clients,
    errors,
    fetchClientSites,
    isLoading,
    open,
    selectedClient,
    selectedSite,
    sites,
    title,
    toggleLoading,
    ...props
}) => {

    const [clientSelected, setClientSelected] = useState(false);

    useEffect(() => {
        if (sites.length > 0) {
            setClientSelected(true);
        } else {
            setClientSelected(false);
        }
    }, [sites]);

    const formik = useFormik({
        initialValues: {
            client:        get(selectedClient, 'id', ''),
            site:          get(selectedSite, 'id', ''),
            name:          '',
            squareFootage: '',
            squareAreaUOM: 'SquareFootage'
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                createArea
            } = props;

            let isCreated;

            const siteId = values.site;

            const body = {
                name:          values.name,
                squareAreaUOM: values.squareAreaUOM,
                squareFootage: values.squareFootage
            };

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createArea(siteId, body);
                }

                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClientChange = async event => {
        const { value } = event.target;

        formik.setFieldValue('site', '', false);

        formik.setTouched({
            site: false
        });

        setClientSelected(true);

        toggleLoading(false);
        await fetchClientSites(value);

        formik.handleChange(event);
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        setClientSelected(false);
        close();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/areaTreeIcon.svg" alt="new area icon" />
                        <span>New Area</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select the associated Client and Site.
                        </p>
                        <p>
                            Choose a unique name for this area.
                        </p>
                        <p>
                            Enter a whole number for the area and select the unit of measure.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="client"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                Client
                            </InputLabel>
                            <Select
                                value={formik.values.client}
                                onChange={handleClientChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'client',
                                    id:     'client',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                {
                                    clients.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.client &&
                             formik.errors.client && <FormHelperText className={classes.error}>{formik.errors.client}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="site"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                Site
                            </InputLabel>
                            <Select
                                value={formik.values.site}
                                onChange={formik.handleChange}
                                className={classes.select}
                                disabled={!clientSelected}
                                inputProps={{
                                    name:   'site',
                                    id:     'site',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                {
                                    sites.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.site &&
                             formik.errors.site && <FormHelperText className={classes.error}>{formik.errors.site}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapper}>
                                <TextField
                                    id="squareFootage"
                                    name="squareFootage"
                                    type="text"
                                    value={formik.values.squareFootage}
                                    label="Area"
                                    onChange={formik.handleChange}
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.squareFootage) && Boolean(formik.touched.squareFootage)}
                                    helperText={Boolean(formik.touched.squareFootage) && formik.errors.squareFootage}
                                />
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="squareAreaUOM"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.squareAreaUOM) && Boolean(formik.touched.squareAreaUOM)}
                                >
                                    Unit Of Measure<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.squareAreaUOM}
                                    onChange={formik.handleChange}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'squareAreaUOM',
                                        id:     'squareAreaUOM',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.squareAreaUOM) && Boolean(formik.touched.squareAreaUOM)}
                                >
                                    {
                                        squareAreaUOMTypes.map(uom =>
                                            <MenuItem
                                                key={uom.type}
                                                value={uom.value}
                                            >
                                                {uom.type}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.squareAreaUOM &&
                                formik.errors.squareAreaUOM && <FormHelperText className={classes.error}>{formik.errors.squareAreaUOM}</FormHelperText>}
                            </FormControl>
                        </div>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateAreaModal.defaultProps = {
    clients: [],
    sites:   []
};

CreateAreaModal.propTypes = {
    clients: array,
    close:   func.isRequired,
    open:    bool.isRequired,
    sites:   array,
    title:   string.isRequired
};

const mapStateToProps = state => {
    return {
        clients:        state.architect.clientNodes,
        companyInfo:    state.companyInfo,
        errors:         state.messages.errors,
        isLoading:      state.ui.isLoading,
        sites:          state.site.sites,
        selectedClient: state.client.selectedClient,
        selectedSite:   state.site.selectedSite,
        treeReset:      state.architect.treeReset
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...SiteActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateAreaModal);
